import { Injectable } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  FormControl,
} from "@angular/forms";
import { ExperimentDesignFormValidatorsService } from "./experiment-design-form-validators.service";
import {
  RecurrencePattern,
  RequestDesign,
} from "../../../shared/services/request.service";
import { PesticideInstruction } from "../../../shared/services/pesticide-instruction.service";
import { ValidatorsService } from "../../../shared/services/validators.service";
import { addDays } from "date-fns";
import { ExperimentDesignShowHideService } from "../../services/experiment-design-show-hide.service";

@Injectable()
export class ExperimentDesignFormBuilderService {
  constructor(
    private formBuilder: FormBuilder,
    private formValidators: ExperimentDesignFormValidatorsService,
    private validatorsService: ValidatorsService,
    private experimentDesignShowHideService: ExperimentDesignShowHideService
  ) {}

  public build(constraints: {
    design: RequestDesign;
    cropId: number;
    plantingDate: Date;
    requestPurposeId: number;
    pesticideInstructions: PesticideInstruction[];
  }): FormGroup {
    return new ExperimentDesignFormService(
      this.formBuilder,
      this.validatorsService,
      this.formValidators,
      this.experimentDesignShowHideService,
      constraints
    ).form;
  }
}

class ExperimentDesignFormService {
  private formGroup: FormGroup;

  public get form(): FormGroup {
    return this.formGroup;
  }

  constructor(
    private formBuilder: FormBuilder,
    private validatorsService: ValidatorsService,
    private formValidators: ExperimentDesignFormValidatorsService,
    private experimentDesignShowHideService: ExperimentDesignShowHideService,
    private constraints: {
      design: RequestDesign;
      cropId: number;
      plantingDate: Date;
      requestPurposeId: number;
      pesticideInstructions: PesticideInstruction[];
    }
  ) {
    this.formGroup = this.formBuilder.group(
      {
        seedNeeds:
          constraints.design && constraints.design.requestedSeed
            ? this.initializeSeedNeedsArray(constraints.design)
            : this.formBuilder.array([]),

        numberOfReps: this.formBuilder.control(
          { value: null, disabled: false },
          [Validators.required, Validators.min(1)]
        ),

        numberOfTreatments: this.formBuilder.control(
          { value: null, disabled: false },
          [Validators.required, Validators.min(1)]
        ),

        numberOfSeedsPerCell: this.formBuilder.control({ value: 1 }, [
          Validators.min(1),
          Validators.max(3),
        ]),

        recurrencePattern: this.formBuilder.control(1),

        recurrenceEndDate: this.formBuilder.control({
          value: null,
          disabled: false,
        }),

        recurrenceWeekInterval: this.formBuilder.control({
          value: null,
          disabled: false,
        }),

        recurrenceDays: this.formBuilder.array([
          this.formBuilder.control(false),
          this.formBuilder.control(false),
          this.formBuilder.control(false),
          this.formBuilder.control(false),
          this.formBuilder.control(false),
        ]),

        treatments: this.formBuilder.array([]),

        harvestInstructions: this.formBuilder.group({
          embryoRescue: [false, [Validators.required]],
          minimumSeedsToReturn: [
            null,
            [Validators.required, Validators.min(0)],
          ],
          forAdvancement: [true, [Validators.required]],
          haploidInduction: [false, [Validators.required]],
          manyToOne: [false, [Validators.required]],
          uniquePedigree: [false, [Validators.required]],
        }),

        indexing: [false, [Validators.required]],
        pesticidesAllowed: [null, [Validators.required]],
        pesticidesAllowedAfterDays: [null, { disabled: true }],
        pesticideLifeOfPlant: [null],
        requestedSamplings: this.formBuilder.array([]),
        sprayStudy: [false],
        pollinationInstructions: this.formBuilder.group({
          pollinationCategory: this.formBuilder.control(null, [
            Validators.required,
          ]),
          pollinationPreferences: this.formBuilder.array(
            [],
            Validators.required
          ),
          donorsNeeded: [false, [Validators.required]],
        }),

        seedRequestDesired: [false, [Validators.required]],
      },
      { validator: this.formValidators.seedNeedsValidator() }
    );

    this.form
      .get("numberOfTreatments")
      .valueChanges.subscribe(numberOfTreatments => {
        if (numberOfTreatments === null || numberOfTreatments === undefined) {
          return;
        }
        this.addOrRemoveTreatments(numberOfTreatments);
        this.addOrRemoveSeedNeedsTreatments(numberOfTreatments);
        const isSprayStudyOn = !!this.form.get('sprayStudy').value;
        this.applySprayStudyValidatorsToAllTreatments(isSprayStudyOn);
        this.experimentDesignShowHideService.updateView(this.formGroup, this.constraints.requestPurposeId);
      });

    const treatmentsFA = this.form.get('treatments') as FormArray;
    treatmentsFA.valueChanges.subscribe(() => {
      this.applySprayStudyValidatorsToAllTreatments(!!this.form.get('sprayStudy')!.value);
    });

    this.form.get("recurrencePattern").valueChanges.subscribe((value) => {
      const recurrenceEndDate = this.form.get("recurrenceEndDate");
      const recurrenceWeekInterval = this.form.get("recurrenceWeekInterval");
      const days = this.form.get("recurrenceDays").value as boolean[];

      if (value === RecurrencePattern.None) {
        recurrenceEndDate.clearValidators();
        recurrenceWeekInterval.clearValidators();
      } else if (value === RecurrencePattern.Weekly) {
        let validDaysOfWeek = [];
        for (let i = 0; i < days.length; i++) {
          if (days[i] === true) validDaysOfWeek.push(i + 1);
        }

        var maxDate = addDays(this.constraints.plantingDate, 30);

        recurrenceEndDate.setValidators([
          Validators.required,
          this.validatorsService.mustBeAfterDate(this.constraints.plantingDate),
          this.validatorsService.mustBeOnOrBeforeDate(maxDate),
          this.validatorsService.allowedDaysOfWeek(validDaysOfWeek),
          this.validatorsService.isValidDate,
        ]);

        recurrenceWeekInterval.setValidators([
          Validators.required,
          Validators.min(1),
        ]);
      }

      recurrenceEndDate.updateValueAndValidity();
      recurrenceWeekInterval.updateValueAndValidity();
    });

    this.form.get("recurrenceDays").valueChanges.subscribe((value) => {
      this.form
        .get("recurrencePattern")
        .updateValueAndValidity({ onlySelf: false, emitEvent: true });
    });

    this.setupPesticideValidation();
  }

  private setupPesticideValidation(): void {
    const pesticidesAllowed = this.formGroup.get("pesticidesAllowed");
    const pesticidesAllowedAfterDays = this.formGroup.get(
      "pesticidesAllowedAfterDays"
    );
    const pesticideLifeOfPlant = this.formGroup.get("pesticideLifeOfPlant");

    const updateFormState = () => {
      const selected = this.constraints.pesticideInstructions.find(
        (i) => i.id === pesticidesAllowed.value
      );
      if (selected && selected.requiresNumberOfDays) {
        pesticideLifeOfPlant.setValidators([Validators.required]);
        if (pesticideLifeOfPlant.value === false) {
          pesticidesAllowedAfterDays.enable();
          pesticidesAllowedAfterDays.setValidators([
            Validators.required,
            Validators.min(1),
          ]);
        } else if (pesticideLifeOfPlant.value === true) {
          pesticidesAllowedAfterDays.setValue(null);
          pesticidesAllowedAfterDays.disable();
          pesticidesAllowedAfterDays.clearValidators();
        } else {
          pesticideLifeOfPlant.setValue(false);
          pesticidesAllowedAfterDays.enable();
          pesticidesAllowedAfterDays.setValidators([
            Validators.required,
            Validators.min(1),
          ]);
        }
      } else {
        pesticideLifeOfPlant.clearValidators();
        pesticideLifeOfPlant.setValue(null);
        pesticidesAllowedAfterDays.setValue(null);
        pesticidesAllowedAfterDays.disable();
        pesticidesAllowedAfterDays.clearValidators();
      }
      pesticideLifeOfPlant.updateValueAndValidity({ emitEvent: false });
      pesticidesAllowedAfterDays.updateValueAndValidity({ emitEvent: false });
    };

    pesticideLifeOfPlant.valueChanges.subscribe(() => {
      setTimeout(() => updateFormState(), 0);
    });
    pesticidesAllowed.valueChanges.subscribe(() => {
      setTimeout(() => updateFormState(), 0);
    });
    setTimeout(() => updateFormState(), 0);
  }

  private buildTreatment(treatmentNumber: number): FormGroup {
    const numberOfSeedsPerCellControl = this.form.get("numberOfSeedsPerCell");

    const treatment = this.formBuilder.group({
      treatmentNumber: treatmentNumber,

      plantingInstructions: this.formBuilder.group(
        {
          numberToStart: [null, [Validators.required, Validators.min(1)]],
          numberToPots: [null, [Validators.required, Validators.min(0)]],
          numberToHarvest: [null, [Validators.required, Validators.min(0)]],
        },
        {
          validator: this.formValidators.plantingInstructionsValidator(0),
        }
      ),

      pottingInstructions: this.formBuilder.group({
        potType: [null, Validators.required],
        substrate: [null, [Validators.required]],
        irrigation: [null, [Validators.required]],
        fertigation: [null, [Validators.required]],
      }),

      flatInstructions: this.formBuilder.group({
        directSeedToPots: [false],
        flatType: [null, [Validators.required]],
        substrate: [null, [Validators.required]],
        irrigation: [null, [Validators.required]],
        fertigation: [null, [Validators.required]],
      }),

      sprayBoothStudy: this.formBuilder.array([
        this.formBuilder.group({
          actives: this.formBuilder.array([]),
          adjuvants: this.formBuilder.array([]),
          applicationTimepointId: [null],
          applicationVolumeId: [null],
        }),
      ]),

      plantHandlingInstructions: this.formBuilder.group({
        lighting: [null, [Validators.required]],
        temperature: [null, [Validators.required]],
        humidity: [null, [Validators.required]],
        imagingNeeded: [false, [Validators.required]],
        sprayStudy: [false, [Validators.required]],
        sprayStudyChemical: [null],
      }),
    });

    numberOfSeedsPerCellControl.valueChanges.subscribe((_) => {
      this.updatePlantingInstructions(treatmentNumber);
    });

    treatment
      .get("plantHandlingInstructions.sprayStudy")
      .valueChanges.subscribe((value) => {
        const sprayStudyChemical = treatment.get(
          "plantHandlingInstructions.sprayStudyChemical"
        );

        if (value === true) {
          sprayStudyChemical.setValidators(Validators.required);
          sprayStudyChemical.updateValueAndValidity();
        } else {
          sprayStudyChemical.clearValidators();
          sprayStudyChemical.updateValueAndValidity();
        }
      });

    treatment
      .get("flatInstructions.directSeedToPots")
      .valueChanges.subscribe((value) => {
        const flatType = treatment.get("flatInstructions.flatType");
        const substrate = treatment.get("flatInstructions.substrate");
        const irrigation = treatment.get("flatInstructions.irrigation");
        const fertigation = treatment.get("flatInstructions.fertigation");

        if (value === true) {
          flatType.clearValidators();
          substrate.clearValidators();
          irrigation.clearValidators();
          fertigation.clearValidators();
        } else {
          flatType.setValidators(Validators.required);
          substrate.setValidators(Validators.required);
          irrigation.setValidators(Validators.required);
          fertigation.setValidators(Validators.required);
        }

        flatType.updateValueAndValidity();
        substrate.updateValueAndValidity();
        irrigation.updateValueAndValidity();
        fertigation.updateValueAndValidity();
      });

    const isSprayStudyOn = !!this.form.get('sprayStudy').value;
    this.applySprayStudyValidatorsToTreatment(treatment, isSprayStudyOn);

    return treatment;
  }

  private applySprayStudyValidatorsToAllTreatments(isOn: boolean): void {
    const treatmentsFA = this.form.get('treatments') as FormArray;
    for (let i = 0; i < treatmentsFA.length; i++) {
      const treatment = treatmentsFA.at(i) as FormGroup;
      this.applySprayStudyValidatorsToTreatment(treatment, isOn);
    }
  }

  private applySprayStudyValidatorsToTreatment(treatment: FormGroup, isOn: boolean): void {
    const sprayBoothArray = treatment.get('sprayBoothStudy') as FormArray;
    sprayBoothArray.controls.forEach(groupCtrl => {
      const sprayBoothGroup = groupCtrl as FormGroup;
      const applicationTimepointId = sprayBoothGroup.get('applicationTimepointId');
      const applicationVolumeId = sprayBoothGroup.get('applicationVolumeId');

      if (isOn) {
        applicationTimepointId.setValidators([Validators.required]);
        applicationVolumeId.setValidators([Validators.required]);
        sprayBoothGroup.setValidators(this.formValidators.atLeastOneActiveOrAdjuvantValidator());
      } else {
        applicationTimepointId.clearValidators();
        applicationVolumeId.clearValidators();
        sprayBoothGroup.clearValidators();
      }

      applicationTimepointId.updateValueAndValidity({ emitEvent: false });
      applicationVolumeId.updateValueAndValidity({ emitEvent: false });
      sprayBoothGroup.updateValueAndValidity({ emitEvent: false });
    });
  }

  private addOrRemoveTreatments(numberOfTreatments: number): void {
    const treatmentArray = this.form.get("treatments") as FormArray;

    if (numberOfTreatments > treatmentArray.length) {
      for (let i = treatmentArray.length; i < numberOfTreatments; i++) {
        treatmentArray.push(this.buildTreatment(i + 1));
      }
    } else if (numberOfTreatments < treatmentArray.length) {
      for (let i = treatmentArray.length; i > numberOfTreatments - 1; i--) {
        treatmentArray.removeAt(i);
      }
    }
  }

  private initializeSeedNeedsArray(design: RequestDesign): FormArray {
    const seedNeedsArray = this.formBuilder.array([]);

    for (let i = 0; i < design.requestedSeed.length; i++) {
      const treatmentArray = this.formBuilder.array([]);
      for (
        let treatmentNumber = 1;
        treatmentNumber <= design.numberOfTreatments;
        treatmentNumber++
      ) {
        const seedCount = this.formBuilder.control(
          { value: 0, disabled: false },
          [Validators.required, Validators.min(0)]
        );

        treatmentArray.push(
          this.formBuilder.group({
            treatmentNumber: this.formBuilder.control({
              value: treatmentNumber,
              disabled: false,
            }),
            seedCount: seedCount,
          })
        );

        seedCount.valueChanges.subscribe((c) => {
          this.updateNumberOfSeedsToStart(treatmentNumber);
        });
      }

      seedNeedsArray.push(
        this.formBuilder.group(
          {
            requestId: this.formBuilder.control({
              value: null,
              disabled: false,
            }),
            entryNumber: this.formBuilder.control({
              value: null,
              disabled: false,
            }),
            siid: this.formBuilder.control({ value: null, disabled: false }),
            geName: this.formBuilder.control({ value: null, disabled: false }),
            numberOfSeeds: this.formBuilder.control({
              value: null,
              disabled: false,
            }),
            treatments: treatmentArray,
          },
          { validator: this.formValidators.seedCountValidator() }
        )
      );
    }

    return seedNeedsArray;
  }

  private addOrRemoveSeedNeedsTreatments(numberOfTreatments: number): void {
    const seedNeeds = <FormArray>this.form.get("seedNeeds");
    if (seedNeeds.length === 0) return;

    const existingArrayLength = (<FormArray>seedNeeds.at(0).get("treatments"))
      .length;
    if (numberOfTreatments === existingArrayLength) return;

    if (numberOfTreatments > existingArrayLength) {
      for (let seedNeed of seedNeeds.controls) {
        const seedNeedTreatmentArray = <FormArray>seedNeed.get("treatments");
        for (
          let treatmentNumber = seedNeedTreatmentArray.length + 1;
          treatmentNumber <= numberOfTreatments;
          treatmentNumber++
        ) {
          const seedCount = this.formBuilder.control(
            { value: 0, disabled: false },
            [Validators.required, Validators.min(0)]
          );
          seedNeedTreatmentArray.push(
            this.formBuilder.group({
              treatmentNumber: this.formBuilder.control({
                value: treatmentNumber,
                disabled: false,
              }),
              seedCount: seedCount,
            })
          );
          seedCount.valueChanges.subscribe((c) => {
            this.updateNumberOfSeedsToStart(treatmentNumber);
          });
        }
      }
    } else if (numberOfTreatments < existingArrayLength) {
      for (let seedNeed of seedNeeds.controls) {
        const seedNeedTreatmentArray = <FormArray>seedNeed.get("treatments");
        for (let i = existingArrayLength; i > numberOfTreatments - 1; i--) {
          seedNeedTreatmentArray.removeAt(i);
        }
      }
    }
    this.clearSeedCounts();
  }

  private updateNumberOfSeedsToStart(treatmentNumber: number) {
    const treatments = (<FormArray>this.formGroup.get("treatments")).at(
      treatmentNumber - 1
    );
    const plantingInstructions = <FormControl>(
      treatments.get("plantingInstructions")
    );
    const numberToStart = <FormControl>(
      plantingInstructions.get("numberToStart")
    );

    let numberToStartTotal = 0;
    const seedNeeds = <FormArray>this.formGroup.get("seedNeeds");
    for (let seedNeed of seedNeeds.controls) {
      const seedNeedsTreatment = (<FormArray>seedNeed.get("treatments")).at(
        treatmentNumber - 1
      );
      numberToStartTotal += seedNeedsTreatment.get("seedCount").value;
    }

    numberToStart.patchValue(numberToStartTotal);
    plantingInstructions.updateValueAndValidity();
    this.updatePlantingInstructions(treatmentNumber);
  }

  private clearSeedCounts(): void {
    const seedNeeds = <FormArray>this.form.get("seedNeeds");
    for (let seedNeed of seedNeeds.controls) {
      const treatmentArray = <FormArray>seedNeed.get("treatments");
      for (let treatment of treatmentArray.controls) {
        const seedCount = treatment.get("seedCount");
        seedCount.patchValue(0);
      }
    }
  }

  private updatePlantingInstructions(treatmentNumber: number) {
    const treatments = (<FormArray>this.formGroup.get("treatments")).at(
      treatmentNumber - 1
    );
    const plantingInstructions = <FormControl>(
      treatments.get("plantingInstructions")
    );

    const numberOfEuids = this.getNumberOfEuids(treatmentNumber);
    plantingInstructions.clearValidators();
    plantingInstructions.setValidators(
      this.formValidators.plantingInstructionsValidator(numberOfEuids)
    );
    plantingInstructions.updateValueAndValidity();
  }

  private getNumberOfEuids(treatmentNumber: number): number {
    const numberOfSeedsPerCellControl = this.form.get("numberOfSeedsPerCell");
    const seedNeeds = <FormArray>this.formGroup.get("seedNeeds");

    let numberOfEuids = 0;
    for (let seedNeed of seedNeeds.controls) {
      const seedNeedsTreatment = (<FormArray>seedNeed.get("treatments")).at(
        treatmentNumber - 1
      );
      numberOfEuids += Math.ceil(
        seedNeedsTreatment.get("seedCount").value /
          numberOfSeedsPerCellControl.value
      );
    }

    return numberOfEuids;
  }
}



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-4/_work/202/s/src/app/request-creation/experiment-design/experiment-design-form/experiment-design-form-builder.service.ts